import type { Data } from '@angular/router';

import type { IEntityInfo } from '@core/models/app.model';
import type { OversightGroupStatus } from '@shared/constants/ume-constants';

export interface IBasicChartConfigurations {
  provided: number;
  total: number;
  lastUpdateTimestamp: string | Date;
  sections: Record<string, ChartSectionColorKey>;
  banner: ChartSectionStatus;
  status?: SpecificChartStatus; // DDQ, DFT, Profile specific status
  isKpiSubmitted?: boolean; // KPI specific flag
  isDisplayChart: boolean; // flag for displaying chart
  hasNewTemplate?: boolean; // flag for DDQ modal
  applicable?: boolean; // Investor area specific flag - card is shown only when true
}

export interface DashboardOversightSummaryBean {
  total: number;
  totalActionRaised: number;
  totalOnBehalf: number;
  totalOnHold: number;
  /** keys are OversightStatus */
  status: Record<
    string,
    {
      parentStatus: OversightGroupStatus;
      count: number;
      actionRaised: number | undefined;
      onBehalf: number | undefined;
      onHold: number | undefined;
    }
  >;
}

export interface DashboardAssessmentSummary {
  total: number;
  status: Record<string, DashboardChartProgressBean>;
}

export interface DashboardChartProgressBean {
  total: number;
}

export interface DashboardVPSummary {
  total: number;
  statusMap: Record<string, number>;
}

export interface INotification {
  entryBeans: INotificationDetails[];
  canConnect: boolean;
  connectingNetworkManagers?: Pick<IEntityInfo, 'guid' | 'companyName'>[];
}

export const NOTIFICATION_GROUPS = {
  TODO: 'TODO',
  FYA: 'FYA',
  FYI: 'FYI',
  FILE: 'FILE',
} as const;

export const GROUP_NAMES = {
  [NOTIFICATION_GROUPS.TODO]: 'To do',
  [NOTIFICATION_GROUPS.FYA]: 'For your attention',
  [NOTIFICATION_GROUPS.FYI]: 'For your information',
  [NOTIFICATION_GROUPS.FILE]: 'File',
} as const;

export type NotificationGroup = (typeof NOTIFICATION_GROUPS)[keyof typeof NOTIFICATION_GROUPS];

export interface INotificationDetails {
  type: DashboardNotificationType;
  notificationGroup: NotificationGroup;
  from: INotificationCompanyInfo[];
  timestamp: Date | string;
  definedCount?: number | null; //screening-related notification
  nEvents?: number | null;
  //details for downloading file
  additionalInfo?: Partial<Data> & {
    fileName: string;
    fileKey: string;
    personMatches: number;
    companyMatches: number;
    //details for the screening tier change notification
    currentTier?: string;
    nextTier?: string;
    pct?: number;
  };
}

export interface INotificationCompanyInfo {
  company: IEntityInfo;
  brId: string;
}

export interface IChartCardInfo {
  icon: 'check' | 'info_outline';
  description: string;
  isShowInfo: boolean;
  decoration: ChartSectionStatus;
}

export interface ICompletionStatus {
  isFilesComplete: boolean;
  isKYCComplete: boolean;
  isKPIComplete: boolean;
}

export const SPECIFIC_CHART_STATUS = {
  NA: 'NA',
  NONE: 'NONE',
  NO_DDQ: 'NO_DDQ',
  DRAFT: 'DRAFT',
  UPDATE: 'UPDATE',
  SUBMITTED: 'SUBMITTED',
  OUTDATED: 'OUTDATED',
  EXPIRING: 'EXPIRING',
  EXPIRED: 'EXPIRED',
  INITIAL: 'INITIAL', // profile
  INCOMPLETE: 'INCOMPLETE', // profile
  COMPLETE: 'COMPLETE', // profile
} as const;

export type SpecificChartStatus =
  (typeof SPECIFIC_CHART_STATUS)[keyof typeof SPECIFIC_CHART_STATUS];

export const VP_CHART_COLORS = {
  DISCARDED: '#f58b00',
  NEW: '#1f9f55',
  SELECTED: '#00b0f2',
} as const;

export type AssessmentStatus = (typeof VP_CHART_COLORS)[keyof typeof VP_CHART_COLORS];

export const SELECTED_FOR_ASSESSMENT_CHART_STATUSES = {
  NEW: 'New in the network',
  SELECTED: 'Selected for assessment',
  DISCARDED: 'Not selected for assessment',
} as const;

export const CHART_TYPE = {
  PROFILE: 'PROFILE',
  DDQ: 'DDQ',
  KYC: 'KYC',
  KPI: 'KPI',
  FILES: 'FILES',
  DFT: 'DFT',
  INVESTOR: 'INVESTOR',
  PM_KPI: 'PM_KPI',
} as const;

export type ChartType = (typeof CHART_TYPE)[keyof typeof CHART_TYPE];

export const DASHBOARD_NOTIFICATION_TYPE = {
  CONNECT_REQUEST_DUE_DILIGENCE: 'CONNECT_REQUEST_DUE_DILIGENCE',
  CONNECT_SHARE_DUE_DILIGENCE_REQUEST: 'CONNECT_SHARE_DUE_DILIGENCE_REQUEST',
  CONNECT_SHARE_DUE_DILIGENCE: 'CONNECT_SHARE_DUE_DILIGENCE',
  COMPLETE_OWN_PROFILE: 'COMPLETE_OWN_PROFILE',
  FOLLOWUP_QUESTION: 'FOLLOWUP_QUESTION',
  RECEIVE_FOLLOWUP_ANSWER: 'RECEIVE_FOLLOWUP_ANSWER',
  ACKNOWLEDGE_SUBMIT_DDQ: 'ACKNOWLEDGE_SUBMIT_DDQ',
  ACKNOWLEDGE_SUBMIT_KPI: 'ACKNOWLEDGE_SUBMIT_KPI',
  ACKNOWLEDGE_SUBMIT_PM_KPI: 'ACKNOWLEDGE_SUBMIT_PM_KPI',
  ACKNOWLEDGE_SUBMIT_DFT: 'ACKNOWLEDGE_SUBMIT_DFT',
  RECEIVE_DDQ: 'RECEIVE_DDQ',
  RECEIVE_KPI: 'RECEIVE_KPI',
  RECEIVE_PM_KPI: 'RECEIVE_PM_KPI',
  RECEIVE_DFT: 'RECEIVE_DFT',
  RECEIVE_FILE: 'RECEIVE_FILE',
  RECEIVE_KYC: 'RECEIVE_KYC',
  RECEIVE_INVESTOR_GENERAL_INFORMATION: 'RECEIVE_INVESTOR_GENERAL_INFORMATION',
  RECEIVE_INVESTMENT_ACCOUNT: 'RECEIVE_INVESTMENT_ACCOUNT',
  ACKNOWLEDGE_SUBMIT_INVESTOR_REQUEST: 'ACKNOWLEDGE_SUBMIT_INVESTOR_REQUEST',
  INVITE_COUNTERPARTIES: 'INVITE_COUNTERPARTIES',
  ACTION_RAISED_BY_UME: 'ACTION_RAISED_BY_UME',
  RECEIVE_PARTIAL_DFT: 'RECEIVE_PARTIAL_DFT',
  SCREENING_CRITICAL_MATCHES_PENDING: 'SCREENING_CRITICAL_MATCHES_PENDING',
  SCREENING_MATCHES_PENDING: 'SCREENING_MATCHES_PENDING',
  SCREENING_MATCHES_REQUIRE_ESCALATION: 'SCREENING_MATCHES_REQUIRE_ESCALATION',
  SCREENING_TIER_CHANGE_PENDING: 'SCREENING_TIER_CHANGE_PENDING',
  ONBOARDING_NOTE_BY_UME: 'ONBOARDING_NOTE_BY_UME',
  RECEIVE_DFT_INCIDENTS: 'RECEIVE_DFT_INCIDENTS',
  RECEIVE_KPI_INCIDENTS: 'RECEIVE_KPI_INCIDENTS',
  RECEIVE_PM_KPI_INCIDENTS: 'RECEIVE_PM_KPI_INCIDENTS',
  FILE_CREATION_COMPLETION: 'FILE_CREATION_COMPLETION',
} as const;

export type DashboardNotificationType =
  (typeof DASHBOARD_NOTIFICATION_TYPE)[keyof typeof DASHBOARD_NOTIFICATION_TYPE];

export const CHART_SECTION_STATUS = {
  ACCESS_DENIED: 'ACCESS_DENIED',
  NOT_REQUESTED: 'NOT_REQUESTED',
  NOT_AVAILABLE: 'NOT_AVAILABLE',
  REQUESTED: 'REQUESTED',
  COMPLETED: 'COMPLETED',
  UPDATED: 'UPDATED',
  REQUESTED_UPDATE: 'REQUESTED_UPDATE',
  INCOMPLETE: 'INCOMPLETE',
  DRAFT: 'DRAFT', // profile
  NO_PROFILE: 'NO_PROFILE',
} as const;

export type ChartSectionStatus = (typeof CHART_SECTION_STATUS)[keyof typeof CHART_SECTION_STATUS];

export const CHART_SECTION_COLOR = {
  GREY: '#EEEEEE',
  RED: '#D61043',
  BLUE: '#009ED8',
  YELLOW: '#f76b00',
} as const;

export type ChartSectionColorKey = keyof typeof CHART_SECTION_COLOR;
